import { UserAddOutlined } from '@ant-design/icons';
import {
  getGetBackProcedimentiPraticaIdSoggettiQueryKey,
  usePostBackProcedimentiPraticaIdSoggetti,
} from '@framework/api-client/api/backoffice/procedimenti/procedimenti.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { FormErrorsAlert } from '@framework/form/form/errors/FormErrorsAlert.tsx';
import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { SubmitButton } from '@framework/form/form/submit/SubmitButton.tsx';
import { TextInput } from '@framework/form/inputs/TextInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { Drawer } from '@framework/ui/components/drawer/Drawer.tsx';
import { DrawerButtonRow } from '@framework/ui/components/drawer/DrawerButtonRow.tsx';
import { Button, message, Space } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import {
  DossierPermissionRequestSchema,
  type IDossierPermissionRequest,
} from './dossier-permission/DossierPermissionRequestSchema.ts';
import { DossierPermissionRoleSelectInput } from './dossier-permission/DossierPermissionRoleSelectInput.tsx';

const TFormItem = FormItem as typeof FormItem<IDossierPermissionRequest>;

interface IDossierPermissionDrawerProps {
  dossierId: string;
}

/**
 * Pulsante che apre il drawer per la gestione dei permessi
 * alla pratica.
 */
export function DossierPermissionDrawer(props: IDossierPermissionDrawerProps) {
  const { dossierId } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const initialValues = useMemo(() => {
    return DossierPermissionRequestSchema.cast({}, { assert: false });
  }, []);

  const queryClient = useQueryClient();

  const save = usePostBackProcedimentiPraticaIdSoggetti();
  const handleSubmit = useCallback(
    async (values: IDossierPermissionRequest) => {
      try {
        await save.mutateAsync({
          data: values,
          idIstanzaPratica: dossierId,
        });
        await queryClient.invalidateQueries(
          getGetBackProcedimentiPraticaIdSoggettiQueryKey(dossierId),
        );
        setOpen(false);
        message.success(
          t(
            'dossier-permission.gestione-permessi-creazione.successo',
            'Permesso creato con successo',
          ),
        );
      } catch (e) {
        message.error(
          getNetworkErrorMessage(
            e,
            t(
              'dossier-permission.gestione-permessi-creazione.errore',
              'Errore durante il salvataggio',
            ),
          ),
        );
      }
    },
    [dossierId, queryClient, save, t],
  );

  return (
    <>
      <Button
        icon={<UserAddOutlined />}
        type="primary"
        onClick={() => {
          setOpen(true);
        }}
      >
        Aggiungi Soggetto
      </Button>
      <Drawer
        title={t(
          'dossier-permission.gestione-permessi-drawer.titolo',
          'Aggiungi nuovo soggetto',
        )}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        destroyOnClose
        size="large"
      >
        <FormHandler
          layout="vertical"
          schema={DossierPermissionRequestSchema}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          <FormErrorsAlert />

          <TFormItem name="nome" label="Nome">
            <TextInput name="nome" />
          </TFormItem>
          <TFormItem name="cognome" label="Cognome">
            <TextInput name="cognome" />
          </TFormItem>
          <TFormItem name="codiceFiscale" label="Codice Fiscale">
            <TextInput name="codiceFiscale" />
          </TFormItem>
          <TFormItem name="ruolo" label="Ruolo">
            <DossierPermissionRoleSelectInput name="ruolo" />
          </TFormItem>

          <DrawerButtonRow>
            <Space>
              <Button
                size="large"
                onClick={() => {
                  setOpen(false);
                }}
              >
                Annulla
              </Button>
              <SubmitButton size="large" />
            </Space>
          </DrawerButtonRow>
        </FormHandler>
      </Drawer>
    </>
  );
}
